import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Loader2, 
  CheckCircle2, 
  AlertCircle, 
  Copy, 
  RefreshCw,
  Calendar,
  CreditCard,
  User,
  Clock,
  PartyPopper,
  Ticket,
  Percent
} from 'lucide-react';

export default function CustomerPaymentPage() {
  const { customerId } = useParams();
  const [loading, setLoading] = useState(true);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [error, setError] = useState(null);
  
  // Estado do PIX
  const [generatingPix, setGeneratingPix] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [newExpiration, setNewExpiration] = useState(null);
  
  // Estado para seleção de plano (clientes Sigma)
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [showFidelidade, setShowFidelidade] = useState(false);
  const [trocandoPlano, setTrocandoPlano] = useState(false);
  
  // Estado para cupom
  const [couponCode, setCouponCode] = useState('');
  const [suggestedCoupon, setSuggestedCoupon] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  
  // Estado para indicação (referral)
  const [referrerPhone, setReferrerPhone] = useState('');
  const [referrerValidated, setReferrerValidated] = useState(null); // {valid, name, id}
  const [validatingReferrer, setValidatingReferrer] = useState(false);
  
  // Polling para verificar pagamento
  const pollingRef = useRef(null);

  useEffect(() => {
    fetchCustomerInfo();
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [customerId]);

  const fetchCustomerInfo = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/payment/${customerId}`);
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Erro ao carregar informações');
      }
      
      const data = await response.json();
      setCustomerInfo(data);
      // Definir plano selecionado como o atual
      setSelectedPlanId(data.plan_id);
      // Se tiver cupom exclusivo do cliente, oferece para ele aplicar com 1 clique (não aplica sozinho)
      if (data.auto_coupon) {
        setSuggestedCoupon(data.auto_coupon);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Validar telefone do indicador
  const validateReferrer = async () => {
    if (!referrerPhone.trim()) return;
    
    setValidatingReferrer(true);
    try {
      const cleanPhone = referrerPhone.replace(/\D/g, '');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/payment/${customerId}/validate-referrer?phone=${cleanPhone}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        setReferrerValidated({ valid: true, name: data.referrer_name, id: data.referrer_id });
        toast.success(`✅ Indicador aplicado: ${data.referrer_name}`);
      } else {
        const error = await response.json();
        setReferrerValidated({ valid: false, error: error.detail });
        toast.error(error.detail || 'Indicador não encontrado');
      }
    } catch (error) {
      setReferrerValidated({ valid: false, error: 'Erro ao validar' });
      toast.error('Erro ao validar indicador');
    } finally {
      setValidatingReferrer(false);
    }
  };
  
  // Validar e aplicar cupom
  const validateCoupon = async (forcedCode) => {
    const code = (forcedCode || couponCode || '').trim();
    if (!code) return;
    
    setValidatingCoupon(true);
    try {
      const planId = selectedPlanId || customerInfo?.plan_id;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/payment/${customerId}/validate-coupon?code=${code.toUpperCase()}&plan_id=${planId || ''}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAppliedCoupon(data);
        toast.success(`✅ Cupom ${data.code} aplicado! Desconto de ${data.discount_percent}%`);
      } else {
        const error = await response.json();
        setAppliedCoupon(null);
        toast.error(error.detail || 'Cupom inválido');
      }
    } catch (error) {
      setAppliedCoupon(null);
      toast.error('Erro ao validar cupom');
    } finally {
      setValidatingCoupon(false);
    }
  };
  
  // Preço final considerando cupom
  const getFinalPrice = () => {
    if (appliedCoupon?.discounted_price !== undefined) {
      return appliedCoupon.discounted_price;
    }
    return customerInfo?.plan_price || 0;
  };

  const [generatingCard, setGeneratingCard] = useState(false);
  const handleGenerateCard = async () => {
    setGeneratingCard(true);
    try {
      const body = { metodo: 'cartao' };
      if (selectedPlanId && selectedPlanId !== customerInfo.plan_id) body.plan_id = selectedPlanId;
      if (appliedCoupon?.code) body.coupon_code = appliedCoupon.code;
      if (referrerValidated?.valid && referrerValidated?.id) body.referrer_phone = referrerPhone.replace(/\D/g, '');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/payment/${customerId}/pix`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Erro ao gerar link de cartão');
      if (data.checkout_url) {
        // guarda a transação pra checar quando voltar e abre o checkout
        try { localStorage.setItem('nexu-card-tx', JSON.stringify({ txId: data.transaction_id, customerId })); } catch {}
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e.message || 'Erro ao gerar link de cartão');
      setGeneratingCard(false);
    }
  };

  const handleGeneratePix = async () => {
    try {
      setGeneratingPix(true);
      
      // Montar body com plan_id, cupom e indicação
      const body = {};
      if (selectedPlanId && selectedPlanId !== customerInfo.plan_id) {
        body.plan_id = selectedPlanId;
      }
      if (appliedCoupon?.code) {
        body.coupon_code = appliedCoupon.code;
      }
      if (referrerValidated?.valid && referrerValidated?.id) {
        body.referrer_phone = referrerPhone.replace(/\D/g, '');
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/payment/${customerId}/pix`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Erro ao gerar PIX');
      }
      
      const data = await response.json();
      setPixData(data);
      toast.success('PIX gerado com sucesso!');
      
      // Iniciar polling para verificar pagamento
      startPaymentPolling(data.transaction_id);
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGeneratingPix(false);
    }
  };

  const startPaymentPolling = (transactionId) => {
    // Limpar polling anterior se existir
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    
    // Verificar a cada 5 segundos
    pollingRef.current = setInterval(async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/public/payment/${customerId}/check/${transactionId}`
        );
        const data = await response.json();
        
        if (data.status === 'approved' && data.renewed) {
          setPaymentConfirmed(true);
          setNewExpiration(data.new_expiration);
          clearInterval(pollingRef.current);
          toast.success('Pagamento confirmado!');
          
          // Mostrar erro do Sigma se houver
          if (data.sigma_error) {
            toast.error(`Aviso: ${data.sigma_error}`, { duration: 10000 });
          }
        } else if (data.status === 'cancelled') {
          clearInterval(pollingRef.current);
          setPixData(null);
          toast.error('Pagamento cancelado ou expirado');
        }
      } catch (err) {
        console.error('Erro ao verificar pagamento:', err);
      }
    }, 5000);
  };

  const handleCopyPix = () => {
    if (pixData?.qr_code_text) {
      navigator.clipboard.writeText(pixData.qr_code_text);
      toast.success('Código PIX copiado!');
    }
  };

  const getStatusBadge = () => {
    if (!customerInfo) return null;
    
    switch (customerInfo.status) {
      case 'ok':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Em dia</Badge>;
      case 'expiring':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Expirando</Badge>;
      case 'expired':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Expirado</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Ops!</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tela de pagamento confirmado
  if (paymentConfirmed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-green-500/10 flex items-center justify-center p-4">
        <div className="text-center space-y-6">
          {/* Animação do círculo de sucesso */}
          <div className="relative mx-auto">
            <div className="w-32 h-32 rounded-full bg-green-500/20 flex items-center justify-center mx-auto animate-pulse">
              <div className="w-24 h-24 rounded-full bg-green-500/40 flex items-center justify-center">
                <CheckCircle2 className="w-16 h-16 text-green-500 animate-bounce" />
              </div>
            </div>
            {/* Anel pulsante */}
            <div className="absolute inset-0 rounded-full border-4 border-green-500/50 animate-ping" 
              style={{ animationDuration: '1.5s' }}
            />
          </div>

          <div className="space-y-2">
            <h2 className="text-3xl font-bold text-green-500 flex items-center justify-center gap-2">
              <PartyPopper className="w-8 h-8" />
              Pagamento Confirmado!
              <PartyPopper className="w-8 h-8 transform scale-x-[-1]" />
            </h2>
            <p className="text-muted-foreground">
              Sua renovação foi processada com sucesso.
            </p>
          </div>
          
          <Card className="max-w-sm mx-auto border-green-500/30">
            <CardContent className="pt-6 pb-6">
              <div className="bg-green-500/10 rounded-xl p-4">
                <p className="text-sm text-muted-foreground mb-1">Nova data de vencimento</p>
                <p className="text-3xl font-bold text-green-500">{newExpiration}</p>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                Você receberá uma mensagem de confirmação no WhatsApp.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full space-y-4">
        {/* Card principal */}
        <Card>
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <CreditCard className="w-8 h-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl">Área de Pagamento</CardTitle>
            <CardDescription>
              Gerencie sua assinatura
            </CardDescription>
          </CardHeader>
          
          <CardContent className="grid lg:grid-cols-2 gap-6 items-start">
            {/* COLUNA ESQUERDA: Informações do cliente */}
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Cliente</p>
                    <p className="font-medium">{customerInfo.name}</p>
                  </div>
                </div>
                {getStatusBadge()}
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Plano Atual</p>
                  <p className="font-medium">{customerInfo.plan_name}</p>
                </div>
              </div>

              {/* FIDELIDADE: banner se recompensa disponível + botão de consultar */}
              {customerInfo.fidelidade?.enabled && (
                <>
                  {customerInfo.fidelidade.recompensa_disponivel ? (
                    <button onClick={() => setShowFidelidade(true)} className="w-full text-left rounded-xl border border-amber-500/40 bg-gradient-to-br from-amber-500/15 to-transparent p-4 mb-4 transition-transform active:scale-[0.99]">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">🎉</span>
                        <div className="flex-1">
                          <p className="font-bold text-amber-600 dark:text-amber-400">Recompensa desbloqueada!</p>
                          <p className="text-xs text-muted-foreground">
                            {customerInfo.fidelidade.recompensa_tipo === 'gratis' ? 'Esta renovação é grátis'
                              : customerInfo.fidelidade.recompensa_tipo === 'dias' ? `+${customerInfo.fidelidade.recompensa_valor} dias grátis`
                              : `${customerInfo.fidelidade.recompensa_valor}% de desconto aplicado`} · toque para ver
                          </p>
                        </div>
                      </div>
                    </button>
                  ) : (
                    <button onClick={() => setShowFidelidade(true)} className="w-full flex items-center justify-between rounded-xl border border-border bg-muted/30 p-3 mb-4 hover:border-amber-500/40 transition-colors">
                      <span className="text-sm font-medium flex items-center gap-2">🏆 Meu cartão fidelidade</span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">{customerInfo.fidelidade.selos}/{customerInfo.fidelidade.meta} selos →</span>
                    </button>
                  )}
                </>
              )}
              
              {/* Plano de renovação (Sigma) — limpo: mostra o atual + alterar */}
              {customerInfo.is_sigma_customer && customerInfo.available_plans?.length > 0 && (() => {
                const planos = customerInfo.available_plans;
                const atual = planos.find(p => p.id === (selectedPlanId || customerInfo.plan_id)) || planos.find(p => p.is_current) || planos[0];
                const temOutros = planos.length > 1;
                return (
                  <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                    {!trocandoPlano ? (
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Plano de renovação</p>
                          <p className="font-medium text-sm truncate">{atual?.name}</p>
                          <p className="text-xs text-muted-foreground">{atual?.duration_months} {atual?.duration_months === 1 ? 'mês' : 'meses'} · R$ {Number(atual?.price || 0).toFixed(2).replace('.', ',')}</p>
                        </div>
                        {temOutros && (
                          <button onClick={() => setTrocandoPlano(true)} className="text-xs text-primary font-medium hover:underline shrink-0">Alterar</button>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium">Escolha o plano</p>
                          <button onClick={() => setTrocandoPlano(false)} className="text-xs text-muted-foreground hover:text-foreground">Fechar</button>
                        </div>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {planos.map((plan) => {
                            const sel = (selectedPlanId || customerInfo.plan_id) === plan.id;
                            return (
                              <button key={plan.id} onClick={() => { setSelectedPlanId(plan.id); setTrocandoPlano(false); }}
                                className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left ${sel ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'}`}>
                                <div>
                                  <p className="font-medium text-sm">{plan.name}</p>
                                  <p className="text-xs text-muted-foreground">{plan.duration_months} {plan.duration_months === 1 ? 'mês' : 'meses'}{plan.is_current ? ' (atual)' : ''}</p>
                                </div>
                                <span className="font-bold text-primary">R$ {Number(plan.price).toFixed(2).replace('.', ',')}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Vencimento</p>
                    <p className="font-medium">{customerInfo.expiration_date}</p>
                  </div>
                </div>
                <span className={`text-sm font-medium ${
                  customerInfo.days_until_expiration < 0 ? 'text-red-500' :
                  customerInfo.days_until_expiration <= 3 ? 'text-yellow-500' :
                  'text-muted-foreground'
                }`}>
                  {customerInfo.days_until_expiration < 0 
                    ? `${Math.abs(customerInfo.days_until_expiration)} dias atrás`
                    : customerInfo.days_until_expiration === 0 
                      ? 'Hoje'
                      : `${customerInfo.days_until_expiration} dias`
                  }
                </span>
              </div>
            </div>

            {/* COLUNA DIREITA: Cupom + Pagamento */}
            <div className="space-y-4">
            {/* Cupom de Desconto */}
            {!pixData && customerInfo.gateway_available && customerInfo.status !== 'ok' && (
              <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Ticket className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Cupom de Desconto</span>
                </div>
                
                {appliedCoupon ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="font-mono font-bold text-green-600">{appliedCoupon.code}</span>
                        <Badge variant="secondary" className="bg-green-500/20 text-green-600">
                          -{appliedCoupon.discount_percent}%
                        </Badge>
                      </div>
                      {!customerInfo.auto_coupon && (
                        <Button variant="ghost" size="sm" onClick={() => { setAppliedCoupon(null); setCouponCode(''); }}>
                          Remover
                        </Button>
                      )}
                    </div>
                    {/* Mostrar economia */}
                    <div className="text-sm text-green-600 dark:text-green-400">
                      <span>De <s className="text-muted-foreground">R$ {appliedCoupon.original_price?.toFixed(2).replace('.', ',')}</s></span>
                      <span className="ml-2 font-bold">por R$ {appliedCoupon.discounted_price?.toFixed(2).replace('.', ',')}</span>
                      <span className="ml-2 text-xs">(economia de R$ {appliedCoupon.savings?.toFixed(2).replace('.', ',')})</span>
                    </div>
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      ⚠️ Ao gerar o PIX com este cupom, ele será marcado como usado mesmo se você não pagar.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Cupom EXCLUSIVO para este cliente — clique para aplicar */}
                    {suggestedCoupon && (
                      <button
                        onClick={() => { setCouponCode(suggestedCoupon.code); const c = suggestedCoupon.code; setSuggestedCoupon(null); validateCoupon(c); }}
                        className="w-full flex items-center justify-between gap-2 p-3 rounded-xl border-2 border-dashed border-green-500/50 bg-green-500/10 hover:bg-green-500/20 transition-all group">
                        <div className="flex items-center gap-2 text-left">
                          <span className="text-xl">🎁</span>
                          <div>
                            <p className="text-sm font-semibold text-green-600 dark:text-green-400">Cupom exclusivo para você!</p>
                            <p className="text-xs text-muted-foreground">
                              <span className="font-mono font-bold">{suggestedCoupon.code}</span>
                              {' · '}
                              {suggestedCoupon.discount_type === 'fixed'
                                ? `R$ ${Number(suggestedCoupon.discount_value || suggestedCoupon.discount_percent || 0).toFixed(2)} de desconto`
                                : `${suggestedCoupon.discount_percent}% de desconto`}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 bg-green-500/20 rounded-lg px-3 py-1.5 group-hover:scale-105 transition-transform">Aplicar</span>
                      </button>
                    )}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Digite o código do cupom"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="font-mono uppercase"
                      />
                      <Button 
                        variant="outline" 
                        onClick={validateCoupon}
                        disabled={validatingCoupon || !couponCode.trim()}
                      >
                        {validatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
                      </Button>
                    </div>
                  </div>
                )}
                
                {customerInfo.min_value > 0 && (
                  <p className="text-xs text-muted-foreground">
                    * Valor mínimo de R$ {customerInfo.min_value.toFixed(2).replace('.', ',')} para este método de pagamento
                  </p>
                )}
              </div>
            )}

            {/* Sistema de Indicação */}
            {!pixData && customerInfo.gateway_available && customerInfo.status !== 'ok' && 
             customerInfo.referral_info?.enabled && customerInfo.referral_info?.can_use && (
              <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-purple-500" />
                  <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Indicação</span>
                  <Badge variant="outline" className="text-xs border-purple-500/50 text-purple-600">
                    Opcional
                  </Badge>
                </div>
                
                {customerInfo.already_referred ? (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Você já utilizou o benefício de indicação anteriormente.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">
                      Foi indicado por alguém? Digite o telefone de quem te indicou e ajude essa pessoa a ganhar uma renovação!
                    </p>
                    
                    <div className="flex gap-2">
                      <Input
                        placeholder="(00) 00000-0000"
                        value={referrerPhone}
                        onChange={(e) => {
                          setReferrerPhone(e.target.value);
                          setReferrerValidated(null);
                        }}
                        className="font-mono"
                      />
                      <Button 
                        variant="outline" 
                        onClick={validateReferrer}
                        disabled={validatingReferrer || !referrerPhone.trim()}
                      >
                        {validatingReferrer ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Validar'
                        )}
                      </Button>
                    </div>
                    
                    {referrerValidated?.valid && (
                      <div className="p-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          Indicador: {referrerValidated.name}
                        </p>
                      </div>
                    )}
                    
                    {referrerValidated && !referrerValidated.valid && (
                      <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          {referrerValidated.error}
                        </p>
                      </div>
                    )}
                    
                    <p className="text-xs text-purple-600 dark:text-purple-400">
                      * Você só pode usar indicação uma vez. Após pagar com indicação, essa opção não aparecerá novamente.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Valor */}
            <div className="text-center py-4 border-y border-border">
              <p className="text-sm text-muted-foreground mb-1">Valor da renovação</p>
              
              {/* Mostrar desconto se houver cupom aplicado */}
              {appliedCoupon?.discounted_price !== undefined ? (
                <div className="space-y-1">
                  <p className="text-lg text-muted-foreground line-through">
                    R$ {appliedCoupon.original_price?.toFixed(2).replace('.', ',')}
                  </p>
                  <p className="text-4xl font-bold text-green-500">
                    R$ {appliedCoupon.discounted_price?.toFixed(2).replace('.', ',')}
                  </p>
                  <Badge className="bg-green-500/20 text-green-600 border-green-500/30">
                    <Percent className="w-3 h-3 mr-1" />
                    {appliedCoupon.discount_percent}% de desconto
                  </Badge>
                </div>
              ) : (
                <p className="text-4xl font-bold text-primary">
                  R$ {(customerInfo.is_sigma_customer && selectedPlanId 
                    ? customerInfo.available_plans?.find(p => p.id === selectedPlanId)?.price || customerInfo.plan_price
                    : customerInfo.plan_price
                  ).toFixed(2).replace('.', ',')}
                </p>
              )}
            </div>

            {/* Botão de renovação ou QR Code */}
            {!pixData ? (
              <div className="space-y-3">
                {customerInfo.gerenciaapp_bloqueado ? (
                  // GerenciaApp: janela de 5 dias
                  <div className="text-center py-6 px-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                    <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                    <p className="font-semibold text-amber-500">Renovação ainda não disponível</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      A renovação fica disponível faltando <strong>{customerInfo.gerenciaapp_janela} dias ou menos</strong> para o vencimento.
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {customerInfo.gerenciaapp_dias > 0
                        ? `Faltam ${customerInfo.gerenciaapp_dias} dias — volte mais perto do vencimento. 😉`
                        : 'Volte mais perto do vencimento. 😉'}
                    </p>
                  </div>
                ) : !customerInfo.gateway_available ? (
                  // Gateway não disponível
                  <div className="text-center py-6">
                    <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                    <p className="font-medium text-yellow-500">Pagamento indisponível</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Entre em contato com {customerInfo.owner_name} para realizar o pagamento.
                    </p>
                  </div>
                ) : customerInfo.checkout_unificado ? (
                  // InfinitePay: um botão só (PIX + cartão no mesmo checkout)
                  <Button 
                    className="w-full h-12 text-lg"
                    onClick={handleGenerateCard}
                    disabled={generatingCard}
                  >
                    {generatingCard ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Abrindo pagamento...</>
                    ) : (
                      <><RefreshCw className="w-5 h-5 mr-2" /> Renovar Agora (PIX ou Cartão)</>
                    )}
                  </Button>
                ) : (
                  // Pode renovar
                  <div className="space-y-2">
                    <Button 
                      className="w-full h-12 text-lg"
                      onClick={handleGeneratePix}
                      disabled={generatingPix || generatingCard}
                    >
                      {generatingPix ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Gerando PIX...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2" />
                          {customerInfo.suporta_cartao ? 'Pagar com PIX' : 'Renovar Agora'}
                        </>
                      )}
                    </Button>
                    {customerInfo.suporta_cartao && (
                      <Button 
                        variant="outline"
                        className="w-full h-12 text-lg border-primary/40"
                        onClick={handleGenerateCard}
                        disabled={generatingPix || generatingCard}
                      >
                        {generatingCard ? (
                          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Abrindo cartão...</>
                        ) : (
                          <><CreditCard className="w-5 h-5 mr-2" /> Pagar com cartão</>
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              // QR Code PIX
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-3">
                    Escaneie o QR Code ou copie o código PIX
                  </p>
                  <div className="bg-white rounded-xl p-4 inline-block">
                    <img 
                      src={pixData.qr_code ? (pixData.qr_code.startsWith('http') ? pixData.qr_code : pixData.qr_code.startsWith('data:') ? pixData.qr_code : `data:image/png;base64,${pixData.qr_code}`) : (pixData.qr_code_url || (pixData.qr_code_text ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(pixData.qr_code_text)}` : ''))}
                      alt="QR Code PIX"
                      className="w-48 h-48"
                    />
                  </div>
                </div>
                
                {/* Código copia e cola */}
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground text-center">PIX Copia e Cola</p>
                  <div className="relative">
                    <input
                      type="text"
                      value={pixData.qr_code_text}
                      readOnly
                      className="w-full p-3 pr-12 bg-muted rounded-lg text-xs font-mono truncate"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="absolute right-1 top-1/2 -translate-y-1/2"
                      onClick={handleCopyPix}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Status do pagamento */}
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Aguardando pagamento...
                </div>
                
                {/* Timer de expiração */}
                <p className="text-xs text-center text-muted-foreground">
                  Este PIX expira em 30 minutos
                </p>
              </div>
            )}
            </div>{/* fim coluna direita */}
          </CardContent>
        </Card>
        
        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          Powered by NEXUS
        </p>
      </div>

      {/* MODAL DO CARTÃO FIDELIDADE */}
      {showFidelidade && customerInfo?.fidelidade?.enabled && (() => {
        const fid = customerInfo.fidelidade;
        const recompensaTxt = fid.recompensa_tipo === 'gratis' ? 'Renovação grátis'
          : fid.recompensa_tipo === 'dias' ? `+${fid.recompensa_valor} dias grátis`
          : `${fid.recompensa_valor}% de desconto`;
        const pct = Math.min(100, Math.round((fid.selos / fid.meta) * 100));
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-200 overflow-y-auto" onClick={() => setShowFidelidade(false)}>
            <div className="w-full max-w-sm my-auto animate-in zoom-in-95 slide-in-from-bottom-4 duration-300" onClick={(e) => e.stopPropagation()}>
              {/* O cartão */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
                {/* brilho decorativo */}
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-20 blur-3xl" style={{ background: '#f5c518' }} />
                <div className="absolute -bottom-20 -left-10 w-48 h-48 rounded-full opacity-10 blur-3xl" style={{ background: '#f5c518' }} />

                <div className="relative p-6 text-white">
                  {/* topo */}
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-amber-300/70">Cartão Fidelidade</p>
                      <p className="text-lg font-bold mt-0.5">{customerInfo.customer_name || customerInfo.plan_name}</p>
                    </div>
                    <div className="text-3xl drop-shadow-lg">🏆</div>
                  </div>

                  {/* selos grandes */}
                  <div className="flex flex-wrap gap-2 justify-center mb-5">
                    {Array.from({ length: fid.meta }).map((_, i) => {
                      const preenchido = i < fid.selos;
                      return (
                        <div key={i} className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-base sm:text-lg font-bold transition-all shrink-0 ${preenchido ? 'shadow-lg' : ''}`}
                          style={preenchido
                            ? { background: 'linear-gradient(135deg, #f5c518, #e0a800)', color: '#1a1a2e', boxShadow: '0 0 12px rgba(245,197,24,0.5)' }
                            : { background: 'rgba(255,255,255,0.06)', border: '1.5px dashed rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.3)' }}>
                          {preenchido ? '★' : (i + 1)}
                        </div>
                      );
                    })}
                  </div>

                  {/* barra de progresso */}
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-2">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #f5c518, #e0a800)' }} />
                  </div>
                  <p className="text-center text-sm text-white/70 mb-5">
                    <span className="font-bold text-amber-300">{fid.selos}</span> de <span className="font-bold">{fid.meta}</span> selos
                  </p>

                  {/* recompensa */}
                  <div className="rounded-2xl bg-white/[0.06] border border-white/10 p-4 text-center">
                    {fid.recompensa_disponivel ? (
                      <>
                        <p className="text-2xl mb-1">🎉</p>
                        <p className="font-bold text-amber-300">Recompensa liberada!</p>
                        <p className="text-sm text-white/80 mt-0.5">{recompensaTxt} nesta renovação</p>
                      </>
                    ) : (
                      <>
                        <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Sua recompensa</p>
                        <p className="font-bold text-lg text-amber-300">{recompensaTxt}</p>
                        <p className="text-xs text-white/60 mt-1">
                          {fid.meta - fid.selos === 1 ? 'Falta só 1 renovação em dia! 🔥' : `Faltam ${fid.meta - fid.selos} renovações em dia`}
                        </p>
                      </>
                    )}
                  </div>

                  <p className="text-center text-[10px] text-white/30 mt-4">Renove sempre em dia para ganhar selos ✨</p>
                </div>
              </div>

              <button onClick={() => setShowFidelidade(false)} className="w-full mt-3 py-3 rounded-xl bg-white/10 text-white text-sm font-medium hover:bg-white/15 transition-colors">
                Fechar
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
